import { ReactNode } from 'react';
import { StatCardRow } from '../organisms';
import { useIsCompact } from '../lib/useMediaQuery';

/**
 * EntityHeader — composed header block for entity-detail pages.
 *
 * Composed pattern (not a single M3 component) per Agent Protocol step 2:
 *   - Composition: optional prefix code chip + title + status pills +
 *     right-aligned action buttons + optional KPI row underneath
 *   - Pattern reference: M3 "Region with supporting info" + Card
 *     Outlined for the surface
 *
 * Populus adaptations applied:
 *   - Single component takes {prefix, title, statusPills, actions, kpis}
 *     props (matches Christophe's client/property/parcel detail screen
 *     header layouts)
 *   - Prefix code chip → high-density entity identifier (e.g., "Parcel
 *     1005301") in monospace; M3 has no equivalent but business-app
 *     density demands it
 *   - Title in Display Small (M3 Display Small spec)
 *   - Right-aligned action cluster (M3 right-aligned actions per
 *     entity-detail conventions)
 *   - Optional KPI row composed via StatCardRow + StatCard from organisms
 *
 * Token discipline:
 *   - Title text → --color-ink (system)
 *   - Prefix chip background → --color-bg (system; subtle gray)
 *   - StatCard accents inherit per-card brand/system token rule (KPI
 *     row composition delegates to the organism's own discipline)
 *
 * Phase 11 Block 6 PR 4 (2026-05-12).
 */

export interface EntityHeaderProps {
  prefix?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  statusPills?: ReactNode;
  actions?: ReactNode;
  kpis?: ReactNode;
}

export function EntityHeader({
  prefix,
  title,
  subtitle,
  statusPills,
  actions,
  kpis,
}: EntityHeaderProps) {
  const compact = useIsCompact();
  return (
    <header style={{ marginBottom: 'var(--sp-6)' }}>
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--sp-4)',
        // Wrap the action cluster below the title on narrow viewports instead
        // of letting the buttons overflow/clip off the right edge (mobile).
        flexWrap: 'wrap',
        marginBottom: kpis ? 'var(--sp-5)' : 0,
      }}>
        <div style={{ flex: '1 1 260px', minWidth: 0 }}>
          {prefix && (
            <div style={{
              display: 'inline-block',
              padding: 'var(--sp-0-5) var(--sp-2)',
              borderRadius: 'var(--r-sm)',
              background: 'var(--color-bg)',
              border: '1px solid var(--color-border)',
              color: 'var(--color-ink-3)',
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--fs-xs)',
              fontWeight: 'var(--fw-medium)',
              marginBottom: 'var(--sp-2)',
            }}>
              {prefix}
            </div>
          )}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--sp-3)',
            flexWrap: 'wrap',
          }}>
            <h1 style={{
              margin: 0,
              fontSize: 'var(--fs-3xl)',
              fontWeight: 'var(--fw-semibold)',
              letterSpacing: 'var(--ls-tight)',
              lineHeight: 'var(--lh-tight)',
              color: 'var(--color-ink)',
            }}>{title}</h1>
            {statusPills && (
              <div style={{
                display: 'flex',
                gap: 'var(--sp-2)',
                alignItems: 'center',
              }}>{statusPills}</div>
            )}
          </div>
          {subtitle && (
            <div style={{
              marginTop: 'var(--sp-1)',
              color: 'var(--color-ink-3)',
              fontSize: 'var(--fs-md)',
            }}>{subtitle}</div>
          )}
        </div>
        {actions && (
          // F7/F8 — the action cluster wraps below the title on a narrow viewport
          // (the outer flexWrap), but it must ALSO be allowed to shrink to that
          // line's width so its OWN flexWrap engages and the buttons wrap onto
          // multiple rows instead of bleeding off the right edge. A fixed
          // flexShrink:0 here kept it at content-width (e.g. Back·Edit·Generate·
          // Mark Prepared·Cancel = ~490px) and overflowed. minWidth:0 + the
          // default shrink lets it fit; desktop rows are wide enough never to
          // shrink, so the desktop layout is unchanged.
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--sp-2)',
            minWidth: 0,
            maxWidth: '100%',
            flexWrap: 'wrap',
            // On mobile the cluster wraps below the title — let it own the full
            // line width so a page can spread its controls across the screen
            // instead of bunching them at content-width. Desktop is untouched.
            flex: compact ? '1 1 100%' : undefined,
          }}>{actions}</div>
        )}
      </div>
      {kpis && (
        <StatCardRow>
          {kpis}
        </StatCardRow>
      )}
    </header>
  );
}
